import LeadCaptureBox from "@/components/LeadCaptureBox";
import EmailGateModal from "@/components/EmailGateModal";
import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { ArrowRight, ChevronRight, CheckCircle, XCircle, AlertTriangle, Loader2 } from "lucide-react";
import { SITE_URL } from "@/lib/siteConfig";
import { callAIJson } from "@/lib/ai";

// ─── Types ────────────────────────────────────────────────────────────────────
type Grade      = "A" | "B" | "C" | "D" | "F";
type CheckStatus = "pass" | "fail" | "warn";

interface CheckItem {
  label:       string;
  status:      CheckStatus;
  detail:      string;
  howToFix?:   string;
}

interface GradeResult {
  domain:      string;
  grade:       Grade;
  score:       number;
  summary:     string;
  checks:      CheckItem[];
  topActions:  string[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function safeArr<T>(v: unknown): T[] { return Array.isArray(v) ? (v as T[]) : []; }

const GRADE_STYLES: Record<Grade, { ring: string; bg: string; text: string; badge: string; label: string }> = {
  A: { ring: "border-teal",      bg: "bg-teal/10",    text: "text-teal",      badge: "bg-teal/10 border-teal/20 text-teal",           label: "Excellent" },
  B: { ring: "border-teal",      bg: "bg-teal/10",    text: "text-teal",      badge: "bg-teal/10 border-teal/20 text-teal",           label: "Good" },
  C: { ring: "border-amber-400", bg: "bg-amber-50",   text: "text-amber-500", badge: "bg-amber-50 border-amber-200 text-amber-600",   label: "Fair" },
  D: { ring: "border-orange-400",bg: "bg-orange-50",  text: "text-orange-500",badge: "bg-orange-50 border-orange-200 text-orange-600",label: "Poor" },
  F: { ring: "border-red-400",   bg: "bg-red-50",     text: "text-red-500",   badge: "bg-red-50 border-red-200 text-red-600",         label: "Critical" },
};

const STATUS_ICON: Record<CheckStatus, React.ReactNode> = {
  pass: <CheckCircle   size={15} className="text-teal flex-shrink-0 mt-px" />,
  fail: <XCircle       size={15} className="text-red-400 flex-shrink-0 mt-px" />,
  warn: <AlertTriangle size={15} className="text-amber-400 flex-shrink-0 mt-px" />,
};

const GRADE_SCORES: Record<Grade, string> = { A: "90–100", B: "75–89", C: "60–74", D: "45–59", F: "0–44" };

const CATEGORIES = [
  { id: "auth",    label: "Authentication",  checks: ["SPF record", "DKIM signing", "DMARC policy", "DMARC reporting"] },
  { id: "domain",  label: "Domain health",   checks: ["Domain age", "Domain reputation", "MX records", "Reverse DNS (PTR)"] },
  { id: "content", label: "Sending practice",checks: ["List hygiene", "Unsubscribe mechanism", "Bounce handling", "Sending consistency"] },
  { id: "infra",   label: "Infrastructure",  checks: ["Dedicated IP", "IP warm-up", "Feedback loops", "BIMI record"] },
];

function isValidDomain(val: string): boolean {
  return /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(val);
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function DeliverabilityGraderPage() {
  const [domain,   setDomain]   = useState("");
  const [loading,  setLoading]  = useState(false);
  const [result,   setResult]   = useState<GradeResult | null>(null);
  const [showGate, setShowGate] = useState(false);
  const [pendingResult, setPendingResult] = useState<GradeResult | null>(null);
  const [error,    setError]    = useState("");
  const resultsRef = useRef<HTMLDivElement>(null);

  async function grade() {
    const val = domain.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    if (!val) { setError("Please enter a domain name."); return; }
    if (!isValidDomain(val)) { setError("Please enter a valid domain (e.g. yourcompany.com)."); return; }
    setError(""); setLoading(true); setResult(null);

    const prompt = `You are an expert email deliverability auditor. Grade the sending domain "${val}" across 16 deliverability checks.

For each check, simulate a realistic assessment. If the domain looks like a legitimate business domain, give mostly passes with some gaps. If it looks suspicious or brand-new, give more failures.

Checks to evaluate across 4 categories:
${CATEGORIES.map(c => `${c.label}: ${c.checks.join(", ")}`).join("\n")}

Scoring: A=90-100 (almost all pass), B=75-89 (most pass, minor gaps), C=60-74 (some important failures), D=45-59 (multiple critical failures), F=0-44 (fundamental problems)

Return ONLY valid JSON:
{
  "grade": "A"|"B"|"C"|"D"|"F",
  "score": <integer 0-100>,
  "summary": "<2 sentences overall assessment>",
  "checks": [
    {"label":"<check name>","status":"pass"|"fail"|"warn","detail":"<1 sentence what was found>","howToFix":"<1 sentence fix — only include for fail/warn>"}
  ],
  "topActions": [<top 3 highest-impact actions, each 1-2 sentences>]
}

Include all 16 checks in order.`;

    try {
      const p = await callAIJson<Partial<GradeResult>>({ prompt, maxTokens: 2000 });
      const grades: Grade[] = ["A","B","C","D","F"];
      setPendingResult({
        domain:     val,
        grade:      grades.includes(p.grade as Grade) ? p.grade as Grade : "C",
        score:      Math.max(0, Math.min(100, parseInt(String(p.score ?? 50)) || 50)),
        summary:    String(p.summary ?? ""),
        checks:     safeArr<CheckItem>(p.checks).map(c => ({
          label:     String(c?.label     ?? ""),
          status:    (["pass","fail","warn"] as const).includes(c?.status as CheckStatus) ? c.status as CheckStatus : "warn",
          detail:    String(c?.detail    ?? ""),
          howToFix:  c?.howToFix ? String(c.howToFix) : undefined,
        })),
        topActions: safeArr<string>(p.topActions).slice(0, 3),
      });
      setShowGate(true);
    } catch (err: unknown) {
      setError("Grading failed: " + (err instanceof Error ? err.message : "Unknown error. Please try again."));
    } finally { setLoading(false); }
  }

  function handleGateComplete() {
    setShowGate(false);
    if (pendingResult) {
      setResult(pendingResult);
      setPendingResult(null);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }

  const gs = result ? GRADE_STYLES[result.grade] : null;
  const passCount = result?.checks.filter(c => c.status === "pass").length ?? 0;
  const failCount = result?.checks.filter(c => c.status === "fail").length ?? 0;
  const warnCount = result?.checks.filter(c => c.status === "warn").length ?? 0;

  // Group checks back into categories for display
  const grouped = result ? CATEGORIES.map((cat, ci) => ({
    ...cat,
    items: result.checks.slice(ci * 4, ci * 4 + 4),
  })) : [];

  return (
    <>
      {showGate && (
        <EmailGateModal toolName="deliverability grade" source="/deliverability-grader"
          onComplete={handleGateComplete} onClose={() => { setShowGate(false); handleGateComplete(); }} />
      )}
      <SeoHead
        title="Free Email Deliverability Grader — MoreMail | Grade Your Domain A–F"
        description="Get an A–F deliverability grade for your sending domain. Checks SPF, DKIM, DMARC, domain reputation, and 12 more signals. Free, instant, no signup."
        url={`${SITE_URL}/deliverability-grader`}
        breadcrumbs={[
          { name: "Home", url: SITE_URL },
          { name: "Deliverability Grader", url: `${SITE_URL}/deliverability-grader` },
        ]}
      />

      {/* Breadcrumb */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate">
          <Link to="/" className="hover:text-ink transition-colors">Home</Link>
          <ChevronRight size={12} />
          <span className="text-ink">Deliverability Grader</span>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-6 text-center">
        <div className="inline-flex items-center px-3 py-1.5 bg-coral/10 border border-coral/20 rounded-full text-coral text-xs font-bold mb-4">
          Free Tool — No signup required
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-ink leading-tight mb-3">
          What's your deliverability{" "}
          <em className="text-coral not-italic">grade?</em>
        </h1>
        <p className="text-slate text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
          Enter your sending domain. We'll grade SPF, DKIM, DMARC, domain reputation, and 12 more signals — and give you an A–F score with a fix list.
        </p>
      </section>

      {/* Input */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-8">
        <div className="bg-white rounded-2xl border border-black/[0.08] overflow-hidden shadow-sm">
          <div className="flex items-center gap-3 px-4 sm:px-5 py-4 border-b border-black/[0.06]">
            <span className="text-slate font-mono text-sm font-medium flex-shrink-0 select-none">@</span>
            <input
              type="text" value={domain} onChange={(e) => { setDomain(e.target.value); setError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter" && !loading) grade(); }}
              placeholder="yourcompany.com"
              maxLength={100} aria-label="Sending domain"
              className="flex-1 min-w-0 bg-transparent text-ink text-sm sm:text-base placeholder-slate/40 focus:outline-none"
            />
          </div>
          <div className="px-4 sm:px-5 py-3.5 bg-paper flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs text-slate/60">16 checks across authentication, domain health, and infrastructure</span>
            <button type="button" onClick={grade} disabled={loading}
              className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 bg-coral text-white text-sm font-semibold rounded-full hover:bg-coral-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md shadow-coral/20 whitespace-nowrap">
              {loading ? <><Loader2 size={14} className="animate-spin flex-shrink-0" />Grading...</> : <>Grade my domain <ArrowRight size={14} /></>}
            </button>
          </div>
        </div>
        {error && <div role="alert" className="mt-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>}
      </section>

      {/* Results */}
      {result && gs && (
        <section ref={resultsRef} className="max-w-3xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">

          {/* Grade + summary */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-5 mb-5 sm:mb-6">
            <div className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 flex-shrink-0 flex flex-col items-center justify-center ${gs.ring} ${gs.bg}`}>
              <span className={`font-display text-4xl font-bold leading-none ${gs.text}`}>{result.grade}</span>
              <span className={`text-[10px] font-bold uppercase tracking-wide mt-1 ${gs.text}`}>{gs.label}</span>
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left w-full">
              <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap mb-2">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-bold ${gs.badge}`}>{result.domain}</span>
                <span className="text-xs font-mono text-slate/60">{result.score}/100 — {GRADE_SCORES[result.grade]}</span>
              </div>
              <p className="text-sm text-slate leading-relaxed">{result.summary}</p>
              <div className="mt-3 flex items-center gap-4 flex-wrap text-xs font-medium">
                <span className="text-teal flex items-center gap-1"><CheckCircle size={12} />{passCount} passed</span>
                <span className="text-amber-500 flex items-center gap-1"><AlertTriangle size={12} />{warnCount} warnings</span>
                <span className="text-red-500 flex items-center gap-1"><XCircle size={12} />{failCount} failed</span>
              </div>
            </div>
          </div>

          {/* Score bar */}
          <div className="bg-white rounded-2xl border border-black/[0.06] p-4 sm:p-5 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-widest text-slate">Overall score</span>
              <span className={`font-display text-lg font-bold ${gs.text}`}>{result.score}/100</span>
            </div>
            <div className="h-3 rounded-full bg-sand overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-700 ${result.grade === "F" || result.grade === "D" ? "bg-red-400" : result.grade === "C" ? "bg-amber-400" : "bg-teal"}`} style={{ width: `${result.score}%` }} />
            </div>
            <div className="flex justify-between text-[10px] font-mono text-slate/40 mt-1.5">
              {(["F","D","C","B","A"] as Grade[]).map(g => <span key={g}>{g}</span>)}
            </div>
          </div>

          {/* Top actions */}
          {result.topActions.length > 0 && (
            <div className="bg-coral/5 border border-coral/15 rounded-2xl p-4 sm:p-5 mb-4">
              <p className="text-xs font-bold uppercase tracking-widest text-coral mb-3">Top {result.topActions.length} actions to improve your grade</p>
              <div className="space-y-2">
                {result.topActions.map((a, i) => (
                  <div key={i} className="flex gap-3 bg-white/60 rounded-xl px-3 sm:px-4 py-3 text-sm">
                    <span className="font-bold text-coral flex-shrink-0">{i + 1}.</span>
                    <span className="text-ink leading-relaxed">{a}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Category check panels */}
          <div className="space-y-4 mb-5">
            {grouped.map((cat) => {
              const catPass = cat.items.filter(c => c.status === "pass").length;
              const catTotal = cat.items.length;
              return (
                <div key={cat.id} className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
                  <div className="px-4 sm:px-5 py-3.5 border-b border-black/[0.06] bg-paper flex items-center justify-between gap-3">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate">{cat.label}</p>
                    <span className={`text-xs font-bold font-mono ${catPass === catTotal ? "text-teal" : catPass >= catTotal / 2 ? "text-amber-500" : "text-red-500"}`}>
                      {catPass}/{catTotal}
                    </span>
                  </div>
                  <div className="divide-y divide-black/[0.04]">
                    {cat.items.map((item, i) => (
                      <div key={i} className="px-4 sm:px-5 py-3 flex items-start gap-3">
                        {STATUS_ICON[item.status]}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-ink">{item.label}</p>
                          <p className="text-xs text-slate/70 mt-0.5 leading-snug">{item.detail}</p>
                          {item.howToFix && (
                            <p className="text-xs text-coral mt-1 leading-snug">→ {item.howToFix}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Grade again */}
          <button type="button" onClick={() => { setResult(null); setDomain(""); }}
            className="w-full mb-5 inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-black/[0.08] text-slate text-sm font-semibold rounded-full hover:bg-paper hover:border-black/[0.14] transition-colors">
            Grade another domain
          </button>

          {/* CTA */}
          <div className="bg-ink rounded-2xl px-5 sm:px-6 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="min-w-0">
              <h3 className="font-display text-lg sm:text-xl font-bold text-white mb-1">Fix your deliverability with MoreMail</h3>
              <p className="text-sm text-white/50">Email warmup, DNS guidance, blacklist monitoring — all in one dashboard.</p>
            </div>
            <LeadCaptureBox />
          </div>
        </section>
      )}

      {/* Social proof / what we check */}
      {!result && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { stat: "16", label: "checks run" },
              { stat: "A–F", label: "letter grade" },
              { stat: "<10s", label: "results time" },
              { stat: "Free", label: "no signup" },
            ].map(({ stat, label }) => (
              <div key={label} className="bg-white rounded-2xl border border-black/[0.06] py-4 text-center">
                <div className="font-display text-xl sm:text-2xl font-bold text-coral">{stat}</div>
                <div className="text-xs text-slate mt-1">{label}</div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-black/[0.06] p-5">
            <p className="text-xs font-bold uppercase tracking-widest text-slate mb-3">What gets checked</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              {CATEGORIES.flatMap(c => c.checks).map((check) => (
                <div key={check} className="flex items-center gap-2 py-1 text-sm text-slate">
                  <span className="w-1 h-1 rounded-full bg-coral flex-shrink-0" />{check}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
